import {Injectable} from '@angular/core';
import {ApiService} from './../api/api.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/internal/operators';
import {newDate} from './../../utils/date';
import {ConfigService} from './../../config/config/config.service';
import {CanSaveInMemory} from './../CanSaveInMemory';
import {generateUUId, isNullOrEmpty} from './../../utils/util';
import {SyncPlanDetails} from './../../contracts/sync-plan-details';
import {OrderViewData} from './../../contracts/order-view-data';
import {serviceApiRoots} from './../../config/config/config.constants';
import {UtilityCookieService as utilityCookie} from './../../utils/cookie/utilitycookie.service';
import {StorageService} from './../storage/storage.service';
import {LoginService} from '../../services/login/login.service';
import {localConsole} from './../../utils/local-console';
import {Order} from './../../contracts/order';

interface OrderDetailsQ {
  expire: number;
  request: string;
  response: any;
}

interface OrdersDateRange {
  startDate: string;
  endDate: string;
}

export const dateRangeMap = {
  'default': -3, // - 3 months by default
  'Last 90 days': -3, // - 3 months
  'Last 6 Months': -6, // - 6 months
  'Last 12 Months': -12, // - 12 months
  'Prior year': 'prior-year'
};
declare const window: any;

@Injectable({
  providedIn: 'root'
})

export class OrderDetailsService extends CanSaveInMemory {

  /**
   * {to: Date, from: Date}
   */
  public range: any;
  /**
   * Next order is pulled on the first load and saved
   */
  public syncPlanOrder: any;
  public nextOrder: any;
  public allOrders: Order[] = [];
  public membersOrders: any[];
  public responseQ: OrderDetailsQ[] = [];
  public syncPlanApproved: boolean;
  public autoFillEligible: boolean;
  public approvalRequired: boolean;
  public isAutoFillEligible: boolean;
  public isAutoFillEnrolled: boolean;
  public isAutofillReviewOrder: boolean;
  public deliveryPreference: string;
  /**
   * presist the value of autofill enrolled if the user is already
   * autofill enrolled, and un-enrolled during active session
   */
  public wasAutoFillEnrolled: boolean;

  /**
   * Subscription of OrderViewData
   * used by other components to be notified once
   * of orderViewData for current box
   *
   * @Usage: asyncOrderDetails.toPromise().then( data => data, err => err)
   */
  public asyncOrderDetails: BehaviorSubject<OrderViewData | any> = new BehaviorSubject({});
  /**
   * Flag for getOrderDetails v2
   */
  public dynamicDashboard: boolean;
  /**
   * Holds latest precessed view data
   */
  public viewOrdersData: OrderViewData;
  /**
   * Placeholder for temporary order id  for orders that don't have id
   */
  public generatedIds = [];
  /**
   * Placeholder for active member responsePayloadData
   */
  public responsePayloadData: any = {};

  constructor(private _apiService: ApiService,
    private _config: ConfigService,
    private storage: StorageService,
    private _auth: LoginService) {
    super();
    this.dynamicDashboard = utilityCookie.assertCookieValue('mdp_dynamic_dashboard', 'on');
  }

  /**
   * Standalone, manual single order view
   * param id | string
   * returns any
   */
  public fetchAndFind(id: string,
    status?: string,
    range?: string): Observable<any> {
    const find = (oid,
      ors,
      sts?) => {
      const m = ors.filter(o => o.orderNumber === oid && (sts
                                                          ? o.orderStatus === sts
                                                          : true));
      return m && m.length
             ? m[0]
             : null;
    };
    const member = this.getMemberDetails();
    return (range
            ? this.get(member, (range === 'last search'
                                ? this.range
                                : this.prepareDateRange(range)), null, true)
            : this.get(member))
      .pipe(map(data => this.pullMembersOrders(data)))
      .pipe(map((ors: any) => find(id, ors, status)));
  }

  /**
   * Standalone manual order fetch with date range
   * param range to: Date, from: Date
   * @returns data {Observable<Array>}
   */
  public filterHistory(range: string): Observable<any> {
    const member = this.getMemberDetails();
    /**
     * Set range
     * {{from: Date; to: Date; opt: any}}
     */
    if (range !== 'last search') {
      this.range = {
        ...this.prepareDateRange(range),
        opt: range
      };
    }
    // console.log(this.range)
    return this.get(member, this.range, null, true)
      .pipe(map(data => this.pullMembersOrders(data)));
  }

  /**
   * Do necessary operation to prepare date range filter
   * param range to: Date, from: Date
   * returns object {{from: Date; to: Date}}
   */
  prepareDateRange(range = 'default'): { from: Date, to: Date } {
    const dateTo = new Date(),
      dateFrom = new Date();
    // need last year 12 months range
    if (dateRangeMap[range] === 'prior-year') {
      // from
      dateFrom.setFullYear(dateFrom.getFullYear() - 1);
      dateFrom.setMonth(0);
      dateFrom.setDate(1);
      // to
      dateTo.setFullYear(dateTo.getFullYear() - 1);
      dateTo.setMonth(11);
      dateTo.setDate(31);
    } else {
      // use a simple map to subtract months
      dateFrom.setMonth(dateFrom.getMonth() + dateRangeMap[range]);
    }
    return {
      from: dateFrom,
      to: dateTo
    };
  }

  /**
   * Prepare date range for getOrderDetails request
   */
  formatDateRange(range: { from: Date, to: Date },
    force?: boolean): OrdersDateRange {
    const dateTo = range
                   ? range.to
                   : (this.range
                      ? this.range.to
                      : new Date());
    const dateFrom = range
                     ? range.from
                     : (this.range
                        ? this.range.from
                        : new Date(dateTo));
    if ((!range && !this.range) || force) {
      // use default
      dateFrom.setMonth(dateFrom.getMonth() + dateRangeMap['default']);
    }
    const from = {
      month: dateFrom.getUTCMonth() + 1, // months from 1-12
      day: dateFrom.getUTCDate(),
      year: dateFrom.getUTCFullYear()
    };
    const to = {
      month: dateTo.getUTCMonth() + 1, // months from 1-12
      day: dateTo.getUTCDate(),
      year: dateTo.getUTCFullYear()
    };
    return {
      startDate: (`${from.year}-${from.month}-${from.day}`),
      endDate: (`${to.year}-${to.month}-${to.day}`)
    };
  }

  /**
   * Fetch orderDetails from API or from memory if called in the last 1 minute
   * GetOrderDetails V2, use cookie mdp_dynamic_dashboard to check if enabled
   *
   * DEV ONLY: Save memberId in sessionStorage to change member
   *
   * test data
   * 11212795488  - Sync Plan - In Process
   * 11212795340 -  2nd  Order - In Process
   * 2517894227 -
   * 11212793902 - Order Submitted
   * 11212795333
   * 11212795326 - Order Submitted
   * 11212795092
   *
   * GetOrderDetails V1
   * param encMemberId | string
   * param range to: Date, from: Date
   * param force | boolean will force calling API if true
   * param plain | boolean if true, will not assert the flags in the response
   * returns orders array {Observable<any>}
   */
  public get(member: any,
    range?: any,
    force?: boolean,
    plain?: boolean): Observable<any> {
    const version2enabled = utilityCookie.assertCookieValue('mdp_dynamic_dashboard', 'on');
    let requestObj: any = {};
    if (version2enabled) {
      serviceApiRoots.set('getOrderDetails', '/Services/ICEAGPV1/multidosepack/1.1.0/');
      const sdaPhase2 = utilityCookie.assertCookieValue('sda_phase2', 'on');
      if (sdaPhase2) {
        serviceApiRoots.set('getOrderDetails', '/Services/ICEAGPV1/multidosepack/1.2.0/');
      }
      requestObj = {
        'requestMetaData': {
          'apiKey': this._config.get('mdp'),
          'appName': this._config.getMdpAppName(),
          'channelName': this._config.get('channelName'),
          'deviceToken': '7777',
          'deviceType': this._config.getDeviceType(),
          'lineOfBusiness': 'ICE',
          'responseFormat': 'JSON',
          'securityType': 'apiKey',
          'source': 'MDP_WEB',
          'type': 'cn-micro-mdporderdetails',
          'conversationID': this.getUUId(),
          'tokenID': member.tokenID || this.storage.sessionStorage.getItem('token')
        },
        'requestPayloadData': {
          'data': {
            'member': {
              'memberID': (member.data
                           ? member.data.encMemberId
                           : (member.encMemberId || member.encRxConnectId)),
              'memberType': 'RXCONNECT'
            }
          }
        }
      };
    } else {
      serviceApiRoots.set('getOrderDetails', '/Services/ICEAGPV1/mdp/1.0.0/');
      requestObj = {
        'requestMetaData': {
          'apiKey': this._config.get('mdp'),
          'appName': this._config.getMdpAppName(),
          'channelName': this._config.get('channelName'),
          'deviceToken': '7777',
          'deviceType': this._config.getDeviceType(),
          'lineOfBusiness': 'ICE',
          'responseFormat': 'JSON',
          'securityType': 'apiKey',
          'source': 'MDP_WEB',
          'type': 'inode',
          'conversationID': this.getUUId(),
          'operationName': 'getOrderDetails'
        },
        'requestPayloadData': {
          'data': {
            'members': [
              {
                'memberID': (member.data
                             ? member.data.encMemberId
                             : (member.encMemberId || member.encRxConnectId)),
                'memberType': 'RXCONNECT'
              }
            ]
          }
        }
      };
    }
    if (range || !version2enabled) {
      Object.assign(requestObj.requestPayloadData, {
        additionalData: this.formatDateRange(range)
      });
    }
    return (!force && this.pullFromMemory(requestObj.requestPayloadData)) || this._apiService.post(
      'getOrderDetails', null, requestObj)
      .pipe(map((res: any) => {
        /**
         * Modify API response before saving in memory
         */
        if (this.orderDetailsResponseValidator(res)) {
          const r = this.responsePayloadData = res.responsePayloadData;
          if (r.members && r.members.length > 0) {
            // loop in members
            r.members.forEach(m => {
              // check if member has orders
              if (m.orders && m.orders instanceof Array) {
                m.orders.forEach(d => {
                  /**
                   // ensure an order number exits for all scenarios
                   // that does not require a real order number
                   **/
                  const uiId = `e${Math.round(Math.random() * 100000000)}`;
                  d.orderNumber = !this._isNullOrEmpty(d.orderNumber)
                                  ? d.orderNumber
                                  : this.isNextOrder(d) && this.nextOrder
                                    ? this.nextOrder.orderNumber
                                    : (this.generatedIds.push(uiId) && uiId);
                });
              }
            });
          }
          /**
           * process data for views and save it
           */
          if (!plain) {
            this.viewOrdersData = this.prepareData(res);
          }
        }
        return res;
      }))
      .pipe(map((res: any) => this.pushToQ(requestObj.requestPayloadData, res, (1000 * 60 * 3))));
  }

  /**
   * Check a list of orders if it contains a next box
   */
  hasNextOrders(list): any | null {
    //  check ( ONLY ) the first record for the orderStatus property
    // to be “MDP_ORDER_SUBMITTED” or 'MDP_ORDER_EDITED'
    const n = list.filter(o => this.isNextOrder(o));
    this.nextOrder = n.length > 0
                     ? n[0]
                     : this.nextOrder;
    return n;
  }

  /**
   * Check if order is a next order
   * param order object
   * returns boolean
   */
  isNextOrder(o: Order): boolean {
    // possible conditions that a next order may have.
    const condition_one = o && (o.orderStatus === 'MDP_ORDER_SUBMITTED' ||
      o.orderStatus === 'MDP_ORDER_PRINTED' ||
      o.orderStatus === 'MDP_ORDER_EDITED' ||
      o.orderStatus === 'MDP_ORDER_APPROVAL_REQUIRED' ||
      o.orderStatus === 'MDP_ORDER_APPROVED' ||
      o.orderStatus === 'MDP_ORDER_AUTOFILL_REVIEW' ||
      o.orderStatus === 'MDP_ORDER_APPROVAL_PAST_DUE');

    // MDP_ORDER_COMPLETED completed but order is still within
    // 2 days of estimatedDeliveryDate
    let withinTwoDays: boolean;
    const estDeliveryDate = newDate(o['estimatedDeliveryDate']);
    const today = new Date();
    // today.setHours(0, 0, 0, 0);
    estDeliveryDate.setDate(estDeliveryDate.getDate() + 2);
    withinTwoDays = estDeliveryDate.getTime() > today.getTime();
    const condition_two = withinTwoDays && (o.orderStatus === 'MDP_ORDER_COMPLETED' || o.orderStatus === 'MDP_ORDER_PICKEDUP');
    return condition_one || condition_two;
  }

  /**
   * Check if order belongs to order history
   * 2 days after estimatedDeliveryDate and order has orderStatus of MDP_ORDER_COMPLETED
   * param order object
   * returns boolean
   */
  isOrderHistory(o: Order): boolean {
    let twoDaysPassed: boolean;
    const estDeliveryDate = newDate(o['estimatedDeliveryDate']);
    const today = new Date();
    // today.setHours(0, 0, 0, 0);
    estDeliveryDate.setDate(estDeliveryDate.getDate() + 2);
    twoDaysPassed = estDeliveryDate.getTime() < today.getTime();
    return o && (o.orderStatus === 'MDP_ORDER_COMPLETED' || o.orderStatus === 'MDP_ORDER_PICKEDUP') && twoDaysPassed;
  }

  /**
   * Check if order requires approval
   */
  isApprovalRequired(order: Order) {
    return order.orderStatus === 'MDP_ORDER_APPROVAL_REQUIRED' || order.orderStatus === 'MDP_ORDER_APPROVAL_PAST_DUE';
  }

  /**
   * Check if order approval Past Due
   */
  isApprovalPastDue(order: Order) {
    return order.orderStatus === 'MDP_ORDER_APPROVAL_PAST_DUE';
  }

  /**
   * Check if order is in review window
   */
  isWithinReviewWindow(order: Order) {
    return order.orderStatus === 'MDP_ORDER_AUTOFILL_REVIEW';
  }

  /**
   * Check if order is in sync plan
   */
  isSyncPlan(order: Order) {
    return order.orderStatus === 'MDP_SYNCPLAN_APPROVED';
  }

  /**
   * Check if orders list include history orders
   * 2 days after estimatedDeliveryDate and order has orderStatus of MDP_ORDER_COMPLETED ||
   * MDP_ORDER_PICKEDUP param list | Array returns any
   */
  hasOrderHistory(list: Order[]): any | null {
    const h = [];
    list.forEach((l,
      i) => {
      if (!this.isNextOrder(l) && this.isOrderHistory(l)) {
        if (l) {
          h.push(l);
        }
      }
    });
    return h.length > 0
           ? h
           : null;
  }

  /**
   * Parse orders from response object
   * TBD: other places to find orders in returned object
   * param response | Object
   * @returns orders Array
   */
  pullMembersOrders(response: any): Order[] {
    const o = [];
    if (response && response.hasOwnProperty('responsePayloadData')) {
      const r = response['responsePayloadData'];
      if (r.members && r.members.length > 0) {
        // loop in members
        r.members.forEach(m => {
          // check if member has orders
          if (m.orders && m.orders instanceof Array) {
            m['orders'].forEach(ord => {
              let d = JSON.parse(JSON.stringify(ord));
              d.memberID = m.memberID;
              d.memberType = m.memberType;
              // check status and drug requestedStatus
              d = this.checkDrugVisibilityStatus(d);
              o.push(d); // add each member order to orders array
            });
          }
        });
        this.allOrders = o;
      }
    }
    /**
     * Restore or create Next order
     * when fetching order history
     * from prior year
     */
    if (this.nextOrder) {
      const genId = this.generatedIds.indexOf(this.nextOrder['orderNumber']) > -1;
      const found = o.some(l => {
        return l.orderNumber === this.nextOrder['orderNumber'];
      });
      if ((!found && !genId) || (!found && genId)) {
        o.push(this.nextOrder);
      }
    }
    this.membersOrders = o;
    return o;
  }

  /**
   * Check drugs to display for status:
   * MDP_ORDER_SUBMITTED': 'Preparing to ship'
   * MDP_ORDER_PRINTED': 'Preparing to ship'
   * MDP_ORDER_COMPLETED': 'Shipped',
   * MDP_ORDER_PICKEDUP': 'Preparing to ship'
   * MDP_ORDER_EDITED': 'Preparing to ship',
   * if retainfornextorder or not
   */
  checkDrugVisibilityStatus(d: Order): Order {
    const shouldCheck = [
      'MDP_ORDER_SUBMITTED',
      'MDP_ORDER_PRINTED',
      'MDP_ORDER_COMPLETED',
      'MDP_ORDER_APPROVAL_REQUIRED',
      'MDP_ORDER_APPROVAL_PAST_DUE',
      'MDP_ORDER_AUTOFILL_REVIEW',
      'MDP_ORDER_APPROVED',
      'MDP_ORDER_PICKEDUP',
      'MDP_ORDER_EDITED'
    ].some(s => s === d.orderStatus);
    if (!shouldCheck) {
      return d;
    }
    if (d.prescriptionInfo && d.prescriptionInfo instanceof Array) {
      d.prescriptionInfo = d.prescriptionInfo.filter((presc: any) => {
        const rs = typeof presc.requestedStatus === 'string' && presc.requestedStatus
          .replace(/\s+/g, '')
          .toLowerCase();
        if ((presc.rxNumber && presc.rxNumber.length > 0) && (presc.storeNumber && presc.storeNumber.length > 0) && (this._isNullOrEmpty(
          presc.requestedStatus) || !rs.match('retain'))) {
          return presc;
        }
      });
    }
    return d;
  }

  hasPreviousHistory(list): any {
    // TBD
  }

  /**
   * Pull primary or first member info from response
   **/
  private pullMemberInfo(response: any): any {
    const member = {};
    if (response && response.hasOwnProperty('responsePayloadData')) {
      const r = response['responsePayloadData'];
      if (r.members && r.members.length > 0 && r.members[0]) {
        Object.assign(member, r.members[0]);
      }
    }
    return member;
  }

  /**
   * Parse isSyncPlanApproved or isAutoFillEnrolled from response object
   * param response | Object
   * @returns isSyncPlanApproved or isAutoFillEnrolled true or false
   */
  public checkMemberActivity(response: any,
    key: string): any {
    const member = this.pullMemberInfo(response);
    if (key === 'deliveryPreference') {
      return member[key]
             ? member[key].toLowerCase()
             : '';
    }
    return (member[key] && member[key].toUpperCase() === 'Y');
  }

  /**
   * Parse Sync Plan orders details from response object
   * param allOrders | Object
   * @returns SyncPlanDetails
   */
  public getSyncPlanDetails(allOrders: any): SyncPlanDetails {
    const orderDetails: SyncPlanDetails = {
      firstOrders: [],
      futureOrders: [],
      address: null,
      orderStoreNumber: null,
      shortFillInfo: false
    };

    if (allOrders && allOrders[0]) {
      const prescriptions = allOrders[0]['prescriptionInfo'];
      if (prescriptions && prescriptions.length > 0) {
        prescriptions.forEach((presc: any) => {
          const orderType: string = (this._isNullOrEmpty(
            presc.requestedStatus) || (presc.requestedStatus && presc.requestedStatus.toLowerCase() === 'included'))
                                    ? 'firstOrders'
                                    : 'futureOrders';

          orderDetails[orderType].push(presc.drugName.toUpperCase());
        });

        orderDetails.firstOrders.sort();
        orderDetails.futureOrders.sort();
      }

      const shrotFillInfo = allOrders[0]['shortFillInfo'];
      if (shrotFillInfo && shrotFillInfo.length > 0) {
        orderDetails.shortFillInfo = true;
      }

      orderDetails.address = {
        addressLine1: allOrders[0]['addressLine1'],
        city: allOrders[0]['city'],
        state: allOrders[0]['state'],
        zip: allOrders[0]['zip']
      };

      orderDetails.orderStoreNumber = !this._isNullOrEmpty(allOrders[0]['orderStoreNumber']);
      this.syncPlanOrder = orderDetails;

      return orderDetails;
    }
    return null;
  }

  /**
   * Get the current active medication box
   */
  currentBox(): Order | null {
    if (this.nextOrder) {
      return this.nextOrder;
    }
    const all = this.allOrders
      .filter(o => this.isOrderHistory(o))
      .sort((oa,
        ob) => newDate(ob['orderCompletedDate']).getTime() - newDate(
        oa['orderCompletedDate']).getTime());
    this.nextOrder = all && all.length
                     ? all[0]
                     : null;
    return this.nextOrder;
  }

  /**
   * Summarize data for view
   * @param data: CVS GetOrderDetails response
   */
  prepareData(data: any): OrderViewData {
    const isMdpEnrolled = this.checkMemberActivity(data, 'isMdpEnrolled');
    const memberInfo = this.pullMemberInfo(data);
    if (!isMdpEnrolled) { // exit if not enrolled
      return {
        noOrders: false,
        allOrders: [],
        nextOrders: [],
        orderHistory: [],
        isMdpEnrolled: isMdpEnrolled,
        memberInfo
      };
    }
    const orders = this.pullMembersOrders(data);

    const isSyncPlanApproved = this.syncPlanApproved = this.checkMemberActivity(data,
      'isSyncPlanApproved') && orders && orders[0].orderStatus === 'MDP_SYNCPLAN_APPROVED';

    const isAutoFillEnrolled = this.isAutoFillEnrolled = this.checkMemberActivity(data,
      'isAutoFillEnrolled');
    this.approvalRequired = orders.length > 0
                            ? this.isApprovalRequired((this.nextOrder || orders[0]))
                            : false;
    this.isAutofillReviewOrder = orders.length > 0
                                 ? this.isAutofillOrderReview(orders[0])
                                 : false;
    this.deliveryPreference = this.checkMemberActivity(data, 'deliveryPreference');
    const viewData = {
      noOrders: orders.length === 0,
      allOrders: orders,
      nextOrders: this.hasNextOrders(orders),
      orderHistory: this.hasOrderHistory(orders),
      isAutoFillEligible: isAutoFillEnrolled,
      isApprovalRequired: this.approvalRequired,
      isAutoFillEnrolled: this.isAutoFillEnrolled,
      wasAutoFillEnrolled: this.wasAutoFillEnrolled
                           ? this.wasAutoFillEnrolled
                           : this.isAutoFillEnrolled,
      dynamicDashboard: this.dynamicDashboard,
      deliveryPreference: this.deliveryPreference,
      isMdpEnrolled: isMdpEnrolled,
      memberInfo
    };
    this.wasAutoFillEnrolled = viewData.wasAutoFillEnrolled;
    if (isSyncPlanApproved) {
      Object.assign(viewData, {
        isSyncPlanApproved: isSyncPlanApproved
      });
    }
    try {
      this.asyncOrderDetails.next(viewData);
    } catch (e) {
      localConsole.log(e);
    }
    return viewData;
  }

  /**
   * Holds logic to determine if autofill eligibility
   * should be run or not
   **/
  public shouldCheckEligibility(order?: Order): boolean {
    const checkAutoFillStatus = this.dynamicDashboard
                                ? utilityCookie.assertCookieValue('mdp_autofill', 'on')
                                : false;
    if (!checkAutoFillStatus) {
      return false;
    }
    if (!this.allOrders.length || this.syncPlanApproved || this.approvalRequired || this.drugOrStoreNumNotMissing(
      (order || this.nextOrder || this.allOrders[0]))) {
      return false;
    }
    return true;
  }

  /**
   * Check if order is MDP_ORDER_AUTOFILL_REVIEW
   **/
  private isAutofillOrderReview(order: Order): boolean {
    return order.orderStatus === 'MDP_ORDER_AUTOFILL_REVIEW';
  }

  private getRxConnId(): string {
    const memberDetails = this.getMemberDetails();
    return memberDetails
           ? memberDetails['encRxConnectId']
           : null;
  }

  /**
   * Retrieve member details from session storage
   */
  private getMemberDetails() {
    let m = window.sessionStorage.getItem('memberDetails');
    if (m) {
      m = JSON.parse(m);
    }
    return m;
  }

  /**
   * @return: string
   * @description: generates a unique id
   */
  private getUUId(): string {
    return generateUUId();
  }

  /**
   * @return: boolean
   * @description: check value for custom invalid null values
   */
  private _isNullOrEmpty(val: any): boolean {
    return isNullOrEmpty(val);
  }

  /**
   * Delete previously saved data
   */
  public flush(): void {
    this.nextOrder = null;
    this.allOrders = [];
    this.membersOrders = [];
    this.responseQ = [];
    this.syncPlanApproved = null;
    this.autoFillEligible = null;
    this.approvalRequired = null;
    this.isAutoFillEligible = null;
    this.isAutoFillEnrolled = null;
    this.wasAutoFillEnrolled = null;
    this.deliveryPreference = null;
    this.viewOrdersData = null;
  }

  /**
   * Validate the response of GetOrderDetails
   */
  private orderDetailsResponseValidator(res: any): boolean {
    return res && res.hasOwnProperty('responsePayloadData');

  }

  /**
   * Validate the order's drugs
   * has store and rx  numbers
   */
  drugOrStoreNumNotMissing(order: Order): boolean {
    return order.prescriptionInfo.some(
      drug => this._isNullOrEmpty(drug.rxNumber) || this._isNullOrEmpty(drug.storeNumber));
  }

}
